import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import React, { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { FieldError, FormLayout, FormRow, FormSection, Select } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { ShiftDifferentialType } from '~/types/hlx/enums';
import { InfoMessage } from '../../../components/InfoMessage';
import { buildShiftAllocationMessage } from '../../../components/shiftAllocationMessage';
import { SHIFT_DIFF_VALUE_LABELS, ShiftTableForm } from '../../../components/ShiftTableForm';
import { resolveRetainedDraft } from '../../../step-machine/retainedDraft';
import { useFlushStepOnExit } from '../../../step-machine/useFlushStepOnExit';
import { useMsaasListingsActorRef } from '../../msaasProvider';
import type { Context as MsaasListingContext } from '../../types';
import { emptyShiftDetailsForm, SHIFT_DIFF_TYPE_OPTIONS } from './constants';
import { ShiftDetailsFormSchema, ShiftDetailsFormValues } from './types';
import { transformApiToShiftDetailsForm } from './utils';

const TAGS = CT.employer.msaasListings.shiftDetailsForm;

export const ShiftDetailsForm = ({ footer }: { footer: React.ReactNode }) => {
  const msaasListingsRef = useMsaasListingsActorRef();
  const { shiftDetailsRef, listing: msaasListing } = useSelector(
    msaasListingsRef,
    (s): MsaasListingContext => {
      if (typeof s === 'object' && s !== null && 'context' in s) {
        return s.context as MsaasListingContext;
      }
      throw new Error('Invalid state: context not found');
    }
  );
  const detail = msaasListing?.detail;

  // [MCV-1543] If the exit flush's write failed, the step machine still holds
  // those values — hydrate from them rather than from a listing.detail the
  // failed write never updated.
  const pendingDraft = useSelector(shiftDetailsRef, (s) => s?.context?.pendingDraft);
  const retainedDraft = resolveRetainedDraft(
    pendingDraft,
    ShiftDetailsFormSchema,
    msaasListing?.detail?.id
  );

  const methods = useCustomForm<ShiftDetailsFormValues>({
    // @ts-expect-error typing for preprocess parsing does not work well
    resolver: zodResolver(ShiftDetailsFormSchema),
    defaultValues:
      retainedDraft ??
      (msaasListing?.detail?.id
        ? transformApiToShiftDetailsForm(msaasListing)
        : emptyShiftDetailsForm),
  });

  const {
    handleSubmit,
    setValue,
    formState: { isDirty, errors },
    watch,
    setError,
    clearErrors,
    getValues,
  } = methods;

  useEffect(() => {
    if (msaasListing) {
      setValue('msaasListingId', msaasListing.id);
      setValue('id', msaasListing.detail?.id);
    }
  }, [msaasListing]);

  // [MCV-1542] The allocation total lives at the top of a tall form while the
  // Next button sits at the bottom, and the mismatch is a form-level (root)
  // error, so react-hook-form's built-in focus-first-error has nothing to focus.
  // Without this the user is told "no" with no indication of where.
  const allocationErrorRef = useRef<HTMLDivElement>(null);
  const revealAllocationError = () => {
    allocationErrorRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  // [MCV-1543] A recovered draft is by definition not persisted, and hydrating
  // from it leaves react-hook-form undirty, so `isDirty` alone would let the
  // step be left again — forwards or backwards — without ever writing it.
  const hasUnsavedWork = isDirty || retainedDraft !== undefined;

  // [MCV-1543] Back-navigation unmounts this form without saving anything.
  useFlushStepOnExit<ShiftDetailsFormValues>({
    save: (values) => shiftDetailsRef.send({ type: 'ON_SAVE', payload: { values } }),
    isDirty: hasUnsavedWork,
    getValues,
    schema: ShiftDetailsFormSchema,
    getCurrentStep: () => msaasListingsRef.getSnapshot().context.currentStep,
  });

  const onSubmitSuccess = (values: ShiftDetailsFormValues) => {
    if (totalShiftsAllocated !== totalShifts) {
      setError('root.shiftAllocation', {
        type: 'custom',
        message: buildShiftAllocationMessage(totalShiftsAllocated, totalShifts),
      });
      revealAllocationError();
      return;
    }
    if (hasUnsavedWork) {
      shiftDetailsRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else {
      msaasListingsRef.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('Shift Details Form Submit Error: ', err, methods.getValues());
  };

  const SHIFT_ALLOCATION_FIELDS = [
    'numDayShifts',
    'numEveShifts',
    'numNightShifts',
    'numOfWeDayShifts',
    'numOfWeEveShifts',
    'numOfWeNightShifts',
    'numHolDayShifts',
    'numHolEveShifts',
    'numHolNightShifts',
  ];

  const shiftsAllocatedFormValues = watch(
    SHIFT_ALLOCATION_FIELDS as (keyof ShiftDetailsFormValues)[]
  );

  const shiftDiffType = watch('shiftDiffType');

  // [MCV-1542] Mirror the two totals the Labor Metrics step calculates. The
  // FTE-only formula produced NaN on an RVU-based listing (numOfFtes is null
  // there), and `allocated !== NaN` is always true — so the allocation gate
  // could never be satisfied and the error the user hit was unclearable.
  const totalShifts = (() => {
    if (!detail) return 0;
    if (detail.laborReqType === 'RVU') {
      return detail.minRvusPerShift
        ? Math.ceil(Number(detail.numRvusReq) / Number(detail.minRvusPerShift))
        : 0;
    }
    return Number(detail.fteShiftsPerYear ?? 0) * Number(detail.numOfFtes ?? 0);
  })();

  let totalShiftsAllocated = 0;
  SHIFT_ALLOCATION_FIELDS.forEach((_field, idx) => {
    totalShiftsAllocated += Number(shiftsAllocatedFormValues[idx]) || 0;
  });

  // [MCV-1542] A root error is not tied to a field, so nothing keeps it in sync
  // as the user corrects the counts. While the message is showing, recompute it
  // on every change: clear it once the totals agree, otherwise re-derive the
  // message so a partial correction (e.g. correcting 800-short down to
  // 300-short) never leaves the OLD count on screen. The message-equality check
  // keeps this a no-op once the text has settled: `errors.root?.shiftAllocation`
  // is a dependency and setError rewrites it, so re-setting an identical message
  // would re-run this effect on every keystroke for no reason.
  useEffect(() => {
    if (!errors.root?.shiftAllocation) return;
    if (totalShiftsAllocated === totalShifts) {
      clearErrors('root.shiftAllocation');
      return;
    }
    const message = buildShiftAllocationMessage(totalShiftsAllocated, totalShifts);
    if (errors.root.shiftAllocation.message !== message) {
      setError('root.shiftAllocation', { type: 'custom', message });
    }
  }, [totalShiftsAllocated, totalShifts, errors.root?.shiftAllocation, setError, clearErrors]);

  const SHIFT_DIFFERENTIAL_FIELDS = [
    '',
    'eveShiftRateDiff',
    'nightShiftRateDiff',
    'weDayShiftRateDiff',
    'weEveShiftRateDiff',
    'weNightShiftRateDiff',
    'holDayShiftRateDiff',
    'holEveShiftRateDiff',
    'holNightShiftRateDiff',
  ];

  const baseDiffStr = (shiftDiffType: ShiftDifferentialType) => {
    switch (shiftDiffType) {
      case ShiftDifferentialType.INCR_HOURLY_RATE:
        return '+$0';
      case ShiftDifferentialType.MULTIPLIER:
        return 'x1.00';
      case ShiftDifferentialType.RVU:
        return msaasListing?.detail?.pricePerRvu != null
          ? `$${msaasListing.detail.pricePerRvu} / RVU`
          : '';
      case ShiftDifferentialType.HOURLY_RATE:
        return msaasListing?.detail?.pricePerHour != null
          ? `$${msaasListing.detail.pricePerHour} / hour`
          : '';
      default:
        return '';
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <InfoMessage
            withOwnMargin={false}
            message={
              'Optional Section: Specify shift distribution and differentials. If skipped, shifts will be evenly distributed with no differentials.'
            }
          />

          <h3 className="sub-heading-sb">Shift Distribution</h3>
          {/* [MCV-1542] `scroll-mt` keeps the row clear of the sticky wizard
            header when revealAllocationError() brings it into view. */}
          <div
            ref={allocationErrorRef}
            className="flex flex-wrap items-center gap-x-3 scroll-mt-24"
          >
            <p className="body-text-sb">
              Total Shifts to Allocate: {totalShiftsAllocated}/{totalShifts}
            </p>
            <ErrorMessage
              errors={errors}
              name="root.shiftAllocation"
              render={({ message }) => (
                // FieldError's default `mt-1.5` positions it *below* a field; inline
                // beside the total, that margin sits the message low against the
                // text it belongs to. className replaces (not appends to) that
                // margin slot, so mt-0 here aligns the message with the total.
                <FieldError message={message} className="mt-0" />
              )}
            />
          </div>
          <ShiftTableForm inputFields={SHIFT_ALLOCATION_FIELDS} tags={TAGS} withOwnMargin={false} />
          <div className="w-full border-t border-grey-900" />
          <h3 className="sub-heading-sb">Shift Differentials from the Weekday Rate</h3>
          <p className="body-text-sb bg-primary-t-900 p-4 rounded-lg">
            Base rate: ${detail?.pricePerHour} ($/hr)
          </p>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Select
                name="shiftDiffType"
                description="Example Multiplier: 1.5"
                cyTag={TAGS.shiftDiffType}
                label="Differential Type"
                options={SHIFT_DIFF_TYPE_OPTIONS}
                tooltip={
                  <div>
                    <p className="mb-2">
                      <b>Incremental Amount:</b> Additional dollar amount per hour added to base
                      rate.
                    </p>
                    <p className="mb-2">
                      <b>Multiplier:</b> Multiply the base rate by a factor
                    </p>
                    <p className="mb-2">
                      <b>RVU Rate:</b> Set a specific RVU rate for the shift, replacing the base RVU
                      rate.
                    </p>
                    <p className="mb-2">
                      <b>Fixed Hourly Rate:</b> Set a specific hourly rate for the shift, replacing
                      the base hourly rate.
                    </p>
                  </div>
                }
              />
            </FormRow>
          </FormSection>
          <ShiftTableForm
            inputFields={SHIFT_DIFFERENTIAL_FIELDS}
            valueLabel={SHIFT_DIFF_VALUE_LABELS[shiftDiffType.value as ShiftDifferentialType]}
            tags={TAGS}
            defaultVal={baseDiffStr(shiftDiffType.value as ShiftDifferentialType)}
            withOwnMargin={false}
          />
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
